import { AnyAction, Middleware } from 'redux';
import { AVAILABLE_ENDORSEMENTS_ACTIONS } from '../actions/availableEndorsements';
import {
  selectEndorsements,
  selectLimitDamagePremRented,
  selectPolicyTypes,
  selectUserHasExistingApplication,
} from '../selectors/application';
import { getAvailableEndorsements } from '../selectors/availableEndorsements';
import {
  setContractorToolsCoverage,
  setCyberCoverage,
  setEndorsementPackage,
  setEPLICoverage,
  setEquipmentBreakdownCoverage,
  setHiredAndNonOwnedAuto,
  setLimitDamagePremRented,
  setLiquorLiabilityCoverage,
  setRestaurantCoverage,
  setWorkplaceViolenceCoverage,
} from '../actions/application';
import { EndorsementOptions } from '../constants';
import { EndorsementKey } from '../Components/Endorsements/types';

// Add-ons that should be cleared when the server stops offering them (e.g.,
// the parent policy was removed). Keyed by the endorsement key in
// state.application.endorsements; value is the clear-action dispatcher.
// LimitDamagePremRented lives at the top of application state instead of
// under endorsements, so it is handled separately below.
type OrphanableEndorsementKey = Exclude<EndorsementKey, EndorsementKey.LimitDamagePremRented>;

const orphanableEndorsements: Record<OrphanableEndorsementKey, () => AnyAction> = {
  [EndorsementKey.CyberCoverage]: () => setCyberCoverage(null),
  [EndorsementKey.WorkplaceViolenceCoverage]: () => setWorkplaceViolenceCoverage(null),
  [EndorsementKey.EpliCoverage]: () => setEPLICoverage(null),
  [EndorsementKey.EquipmentBreakdownEndorsement]: () => setEquipmentBreakdownCoverage(false),
  [EndorsementKey.RestaurantCoverage]: () => setRestaurantCoverage(false),
  [EndorsementKey.LiquorLiabilityEndorsement]: () => setLiquorLiabilityCoverage(null),
  [EndorsementKey.HiredAutoAndNonOwnedAuto]: () => setHiredAndNonOwnedAuto(null),
  [EndorsementKey.ContractorsInstallationAndTools]: () => setContractorToolsCoverage(null),
};

const isUserSelected = (value: unknown) => value != null && value !== false;

const setEndorsementDefaults: Middleware<{}, ReduxState> = (store) => (next) => (action) => {
  const result = next(action);
  const { type } = action;

  if (type === AVAILABLE_ENDORSEMENTS_ACTIONS.SET_AVAILABLE_ENDORSEMENTS) {
    const state = store.getState();
    const endorsements = selectEndorsements(state);
    const availableEndorsements = getAvailableEndorsements(state);
    const isExistingApplication = selectUserHasExistingApplication(state);

    // Orphan cleanup: clear any user-selected add-on that the server no
    // longer offers. Runs for both new and existing applications so that
    // dropping a policy reliably cleans up add-ons that would otherwise
    // block bind and strand the user on Summary.
    (Object.entries(orphanableEndorsements) as [OrphanableEndorsementKey, () => AnyAction][]).forEach(
      ([key, clearAction]) => {
        const userHasIt = isUserSelected(endorsements?.[key]);
        const serverOffersIt = availableEndorsements?.[key] != null;
        if (userHasIt && !serverOffersIt) {
          store.dispatch(clearAction());
        }
      }
    );

    // limitDamagePremRented lives at the top of application state, not under
    // endorsements, and the server doesn't always enumerate it as a key in
    // availableEndorsements even when it's valid (e.g., bridged BOP quotes).
    // Gate cleanup on policy types instead: it's only valid for BOP or GL,
    // limitDamagePremRented lives at the top of application state, not under
    // endorsements.  if application type does not have a BOP or GL strip out limitDamagePremRented
    const limitDamagePremRented = selectLimitDamagePremRented(state);
    const policyTypes = selectPolicyTypes(state);
    const supportsLimitDamagePremRented = policyTypes.includes('BOP') || policyTypes.includes('GL');
    if (limitDamagePremRented != null && !supportsLimitDamagePremRented) {
      store.dispatch(setLimitDamagePremRented(null));
    }

    // Do not set default endorsements if the application is an existing application
    // (the user has come directly from /application/id). Cleanup above still runs.
    if (isExistingApplication) {
      return result;
    }

    const currentCyberCoverage = endorsements?.cyberCoverage;
    const currentWorkplaceViolenceCoverage = endorsements?.workplaceViolenceCoverage;
    const currentEPLICoverage = endorsements?.epliCoverage;
    const isEquipmentBreakdownAvailable = Boolean(
      availableEndorsements?.equipmentBreakdownEndorsement &&
        JSON.parse(availableEndorsements.equipmentBreakdownEndorsement)
    );
    const isRestaurantCoverageAvailable = Boolean(
      availableEndorsements?.restaurantCoverage && JSON.parse(availableEndorsements.restaurantCoverage)
    );
    const isGoldPackageAvailable = availableEndorsements?.endorsementPackage?.includes(EndorsementOptions.GOLD);

    // this condition should be true only if the user hasn't already deselected cyber coverage
    if (availableEndorsements?.cyberCoverage && currentCyberCoverage === undefined) {
      store.dispatch(setCyberCoverage(JSON.parse(availableEndorsements.cyberCoverage)));
    }

    // this condition should be true only if the user hasn't already deselected WPV coverage
    if (availableEndorsements?.workplaceViolenceCoverage && currentWorkplaceViolenceCoverage === undefined) {
      store.dispatch(setWorkplaceViolenceCoverage(JSON.parse(availableEndorsements.workplaceViolenceCoverage)));
    }

    // this condition should be true only if the user hasn't already deselected EPLI coverage
    if (availableEndorsements?.epliCoverage && currentEPLICoverage === undefined) {
      store.dispatch(setEPLICoverage(JSON.parse(availableEndorsements.epliCoverage)));
    }

    if (isEquipmentBreakdownAvailable) {
      store.dispatch(setEquipmentBreakdownCoverage(true));
    }

    if (isRestaurantCoverageAvailable) {
      store.dispatch(setRestaurantCoverage(true));
    }

    if (isGoldPackageAvailable) {
      store.dispatch(setEndorsementPackage(EndorsementOptions.GOLD));
    }
  }

  return result;
};

export default setEndorsementDefaults;
